<template>
    <Prime-Select
        :modelValue="modelValue"
        :options="frequencySets"
        optionLabel="name"
        optionValue="id"
        filter
        :placeholder="placeholder"
        :pt="passThrough"
        @update:modelValue="$emit('update:modelValue', $event)"
        @change="$emit('change', $event)"
    >
        <template #value="{ placeholder: valuePlaceholder }">
            <div
                v-if="selectedFrequencySet"
                class="frequency-set-selected"
                :data-test-id="`frequency-set-selected-${selectedFrequencySet.id}`"
            >
                <span
                    class="name"
                    :data-test-id="`frequency-set-selected-${selectedFrequencySet.id}-label`"
                >
                    {{ selectedFrequencySet.name }}
                </span>
                <frequency-set-type-badge
                    :type="selectedFrequencySet.type"
                    :data-test-id="`frequency-set-selected-${selectedFrequencySet.id}-type`"
                />
            </div>
            <span v-else>{{ valuePlaceholder }}</span>
        </template>
        <template #option="{ option }">
            <div
                class="frequency-set-option"
                :data-test-id="`frequency-set-option-${option.id}`"
            >
                <span :data-test-id="`frequency-set-option-${option.id}-label`">
                    {{ option.name }}
                </span>
                <frequency-set-type-badge
                    :type="option.type"
                    :data-test-id="`frequency-set-option-${option.id}-type`"
                />
            </div>
        </template>
        <template
            v-if="$slots.footer"
            #footer
        >
            <slot name="footer" />
        </template>
    </Prime-Select>
</template>

<script>
import PrimeSelect from 'primevue/select'
import FrequencySetTypeBadge from '@/components/FrequencySetTypeBadge.vue'

export default {
    components: {
        PrimeSelect,
        FrequencySetTypeBadge,
    },

    props: {
        modelValue: {
            type: Number,
            default: null,
        },
        // Each frequency set needs an id and a name;
        frequencySets: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: 'Select a Frequency Set',
        },
        testId: {
            type: String,
            default: 'frequency-set-list',
        },
    },

    emits: ['update:modelValue', 'change'],

    computed: {
        selectedFrequencySet() {
            return this.frequencySets.find((frequencySet) => frequencySet.id === this.modelValue) ?? null
        },

        passThrough() {
            return {
                root: {
                    'data-test-id': this.testId,
                },
                list: {
                    'data-test-id': `${this.testId}-options`,
                },
                pcFilter: {
                    root: {
                        'data-test-id': 'frequency-set-list-search-box',
                    },
                },
            }
        },
    },
}
</script>

<style scoped lang="scss">
// PrimeVue's select has no fixed height, so it grows to fit its content. A selected
// value is taller than a line of text because of the type badge, which would make the
// field jump taller the moment a set is picked. Pin it to the height the app gives its
// other form controls (see input/select in _form.scss) and centre the value inside.
.p-select {
    height: 36px;

    :deep(.p-select-label) {
        display: flex;
        align-items: center;
        padding-block: 0;
    }
}

.frequency-set-option {
    display: flex;
    align-items: center;
    gap: 8px;
}

.frequency-set-selected {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    .name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .p-badge {
        flex-shrink: 0;
    }
}
</style>
